import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Clock, Loader2, MapPin, Users } from 'lucide-react';
import { eventsApi, Event } from '../services/eventsApi';
import { AuthPromptModal } from '../components/AuthPromptModal';
import { useAuthGate } from '../hooks/useAuthGate';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] as const },
};

export default function LandingEventsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isAuthed,
    userId,
    requireAuth,
    authPromptOpen,
    closeAuthPrompt,
    confirmAuthPrompt,
  } = useAuthGate();

  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    return () => document.documentElement.classList.remove('landing-page');
  }, []);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getAll(),
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ['user-registrations', userId],
    queryFn: () => eventsApi.getUserRegistrations(userId!),
    enabled: isAuthed && !!userId,
  });

  const registeredIds = new Set(registrations.map((r) => r.eventId));

  const registerMutation = useMutation({
    mutationFn: (eventId: string) => eventsApi.register(userId!, eventId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  const unregisterMutation = useMutation({
    mutationFn: (eventId: string) => eventsApi.unregister(userId!, eventId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  const today = new Date().toISOString().split('T')[0];
  const upcoming = events
    .filter((e) => e.date.split('T')[0] >= today)
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="landing-root min-h-screen text-white" style={{ background: '#0a0a0a' }}>
      <header className="flex items-center justify-between gap-4 px-5 sm:px-10 pt-6 pb-4">
        <Link
          to="/"
          className="text-lg font-bold uppercase"
          style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
        >
          Теория
        </Link>
        <nav className="flex items-center gap-5">
          <Link
            to="/menu"
            className="text-[12px] tracking-[0.18em] uppercase font-semibold transition-opacity hover:opacity-70"
            style={{ fontFamily: 'Manrope, sans-serif', color: 'rgba(255,255,255,0.7)' }}
          >
            Меню
          </Link>
          <Link
            to="/app/home"
            className="text-[12px] tracking-[0.18em] uppercase font-semibold transition-opacity hover:opacity-70"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            В приложение
          </Link>
        </nav>
      </header>

      <main className="px-5 sm:px-10 pb-20 max-w-3xl mx-auto pt-10 sm:pt-16">
        <motion.div {...fadeUp}>
          <p
            className="text-[11px] tracking-[0.3em] uppercase mb-5"
            style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
          >
            Афиша
          </p>
          <h1
            className="text-4xl sm:text-5xl font-bold uppercase mb-4"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
          >
            События
          </h1>
          <p
            className="text-base sm:text-lg leading-relaxed mb-12 max-w-xl"
            style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Manrope, sans-serif' }}
          >
            Мастер-классы, дегустации и встречи. Запишитесь заранее — места ограничены.
          </p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin" style={{ color: 'rgba(255,255,255,0.35)' }} />
          </div>
        ) : upcoming.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}>
            Сейчас нет ближайших событий. Загляните позже.
          </p>
        ) : (
          <div className="space-y-4">
            {upcoming.map((event, i) => (
              <LandingEventCard
                key={event.id}
                event={event}
                index={i}
                isRegistered={registeredIds.has(event.id)}
                isPending={
                  (registerMutation.isPending && registerMutation.variables === event.id) ||
                  (unregisterMutation.isPending && unregisterMutation.variables === event.id)
                }
                onDetail={() => navigate(`/events/${event.id}`)}
                onRegister={() =>
                  requireAuth(`/events/${event.id}`, () => registerMutation.mutate(event.id))
                }
                onUnregister={() =>
                  requireAuth(`/events/${event.id}`, () => unregisterMutation.mutate(event.id))
                }
                guestLabel={!isAuthed}
              />
            ))}
          </div>
        )}
      </main>

      <AuthPromptModal
        open={authPromptOpen}
        onClose={closeAuthPrompt}
        onConfirm={confirmAuthPrompt}
      />

      <footer className="px-5 sm:px-10 py-10 border-t border-white/10">
        <div className="max-w-3xl mx-auto flex flex-wrap gap-4 justify-between text-[12px] tracking-[0.12em] uppercase">
          <Link to="/" style={{ fontFamily: 'Manrope, sans-serif' }} className="hover:opacity-70">
            На главную
          </Link>
          <Link to="/app/home" style={{ fontFamily: 'Manrope, sans-serif' }} className="hover:opacity-70">
            Приложение
          </Link>
        </div>
      </footer>
    </div>
  );
}

function LandingEventCard({
  event,
  index,
  isRegistered,
  isPending,
  guestLabel,
  onDetail,
  onRegister,
  onUnregister,
}: {
  event: Event;
  index: number;
  isRegistered: boolean;
  isPending: boolean;
  guestLabel?: boolean;
  onDetail: () => void;
  onRegister: () => void;
  onUnregister: () => void;
}) {
  const dateObj = new Date(event.date);
  const formattedDate = dateObj.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    weekday: 'short',
  });

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.45 }}
      className="rounded-3xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {event.image && (
        <button type="button" onClick={onDetail} className="block w-full h-48 relative text-left">
          <img src={event.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <span
            className="absolute bottom-4 left-4 text-xs tracking-[0.14em] uppercase font-semibold"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {formattedDate}
          </span>
        </button>
      )}

      <div className="p-5 sm:p-6">
        {!event.image && (
          <p
            className="text-[11px] tracking-[0.2em] uppercase mb-2"
            style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
          >
            {formattedDate}
          </p>
        )}
        <button type="button" onClick={onDetail} className="text-left w-full">
          <h2
            className="text-xl sm:text-2xl font-bold mb-2"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
          >
            {event.title}
          </h2>
        </button>
        {event.description && (
          <p
            className="text-sm leading-relaxed mb-4 line-clamp-3"
            style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Manrope, sans-serif' }}
          >
            {event.description}
          </p>
        )}

        <div className="flex flex-wrap gap-3 mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {event.time && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium">
              <Clock size={13} /> {event.time}
            </span>
          )}
          {event.location && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium">
              <MapPin size={13} /> {event.location}
            </span>
          )}
          {event.registrationsCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium">
              <Users size={13} /> {event.registrationsCount}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={isRegistered ? onUnregister : onRegister}
            disabled={isPending}
            className="px-5 py-3 rounded-2xl text-sm font-semibold tracking-[0.08em] uppercase transition-all active:scale-95 disabled:opacity-50"
            style={
              isRegistered
                ? {
                    background: 'transparent',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    fontFamily: 'Manrope, sans-serif',
                  }
                : { background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }
            }
          >
            {isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : isRegistered ? (
              'Отменить запись'
            ) : guestLabel ? (
              'Войти и записаться'
            ) : (
              'Записаться'
            )}
          </button>
          <button
            type="button"
            onClick={onDetail}
            className="px-5 py-3 rounded-2xl text-sm font-semibold tracking-[0.08em] uppercase"
            style={{
              color: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(255,255,255,0.15)',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Подробнее
          </button>
        </div>
      </div>
    </motion.article>
  );
}
